import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Autocomplete,
  TextField,
  OutlinedInput,
  FormControl,
  InputAdornment,
  Backdrop,
  CircularProgress,
  Breadcrumbs,
  Link,
  MenuItem,
  Select,
} from '@mui/material';
import { TabContext } from '@mui/lab';
import KeyboardDoubleArrowRightSharpIcon from '@mui/icons-material/KeyboardDoubleArrowRightSharp';
import {
  ReactFlowProvider,
  ReactFlow,
  addEdge,
  Controls,
  useNodesState,
  useEdgesState,
  Background,
  BackgroundVariant,
} from 'reactflow';
import '../../../../App.css';
import { useGoogleLogin } from '@react-oauth/google';
import { useMsal } from '@azure/msal-react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ReactComponent as TuneIcon } from '../../../../assets/common-assets/tool.svg';
import { ReactComponent as SearchIcon } from '../../../../assets/common-assets/search.svg';
import { ReactComponent as KeyboardDoubleArrowLeftIcon } from '../../../../assets/common-assets/leftArrow.svg';
import { ReactComponent as Visibility } from '../../../../assets/common-assets/eye.svg';
import { ReactComponent as PlayArrowIcon } from '../../../../assets/common-assets/play-circle.svg';
import { ReactComponent as StopArrowIcon } from '../../../../assets/common-assets/stop-circle.svg';
import { ReactComponent as AddIcon } from '../../../../assets/common-assets/Add.svg';
import { ReactComponent as FolderOpenIcon } from '../../../../assets/common-assets/folderPlus.svg';
import InputOutputApplications from './InputOutputApplications';
import { globalClasses } from '../../utils/globalUtils';
import { ReactComponent as GoogleDriveIcon } from '../../../../assets/uploadFile-assets/google-drive.svg';
import { ReactComponent as OneDriveIcon } from '../../../../assets/uploadFile-assets/one-drive.svg';
import { ReactComponent as SharePointIcon } from '../../../../assets/uploadFile-assets/sharepoint.svg';
import { ReactComponent as GmailIcon } from '../../../../assets/uploadFile-assets/gmail.svg';
import { ReactComponent as OutlookIcon } from '../../../../assets/uploadFile-assets/outlook.svg';
import { ReactComponent as LogoIcon } from '../../../../assets/common-assets/logoIcon.svg';
import { ReactComponent as Zoho } from '../../../../assets/common-assets/zoho.svg';
import { ReactComponent as Xero } from '../../../../assets/common-assets/xero.svg';
import { ReactComponent as Odoo } from '../../../../assets/common-assets/odoo.svg';
import { ReactComponent as AddUserIcon } from '../../../../assets/common-assets/user-plus.svg';
import { ReactComponent as QuickBook } from '../../../../assets/common-assets/quickBook.svg';
import { ReactComponent as DeleteAccount } from '../../../../assets/allInvoice-assets/trash-2.svg';
import { ReactComponent as CloseIcon } from '../../../../assets/accountSetting-assets/x-circle.svg';
import { ReactComponent as Tally } from '../../../../assets/common-assets/tally.svg';
import { lightPalette } from '../../../../theme';
import {
  editInvoiceConfigurationFlow,
  getFlowById,
  updateDetails,
  getAllUserFromCredential,
  zohoAuthentication,
  tokenExchange,
  deleteUserFromCredential,
  tokenExchangeForXero,
  xeroAuthentication,
  microsoftAuthentication,
  tokenExchangeForMicrosoft,
} from '../../../../services/flowConfiguration';
import {
  createToken,
  googleUploadFlow,
  microSoftUploadFlow,
} from '../../../../services/uploadApi';
import googleDriveFolderPath from './googleDriveFolderPath';
import {
  fetchFolderDetails,
  openAuthenticationEmailPicker,
  openPathSelectionPicker,
} from './oneDriveAutomationFlow';
import { Root, StyledTabList, Tabs, classes } from './utils';
import Toast from '../../utils/Toast';

import CustomEdge from './CustomEdge';
import CustomNodeType from './CustomNodeType';
import 'reactflow/dist/style.css';
import { logInfo } from '../../../../services/flowLogs';
import FilterPopUp from '../upload-documents/FilterPopUp';
import OdooForm from './OdooForm';
import {
  QB_CLIENT_ID,
  THIRD_PARTY_REDIRECT_URL_URL_FOR_AUTOMATED,
} from '../../../../constants/Constants';
import { exportQB } from '../../../../constants/staticContent';
import CustomTooltip from '../../utils/CustomTooltip';
import { checkDocutranscribeNode } from './nodeConnection';
import {
  createManualAuthCredential,
  tokenExchangeForQB,
} from '../../../../services/exportApi';
import { connectSocket } from '../../../../store/socketConfigSlice';
import { completeNotification } from '../../../../store/socketSlice';
import TallyPrimeForm from './TallyPrimeForm';
import getAllGoogleToken from '../../../../services/authenticationApi';
import TaxMapping from './TaxMapping';
import DisplayNamePopUp from '../documents/DisplayNamePopUp';

const sidebarWidth = 240;
const collapsedWidth = 60;
const defaultFilterData = [
  [
    {
      filter: '',
      filterName: '',
      condition: '',
      inputValue: '',
    },
  ],
];
const iconsMap = {
  Gmail: <GmailIcon className={classes.logoStyle} />,
  GoogleDrive: <GoogleDriveIcon className={classes.logoStyle} />,
  Outlook: <OutlookIcon className={classes.logoStyle} />,
  OneDrive: <OneDriveIcon className={classes.logoStyle} />,
  SharePoint: <SharePointIcon className={classes.logoStyle} />,
};

const docuTranscribeIcon = {
  Docutranscribe: <LogoIcon className={classes.logoStyle} />,
};

const exportIcon = {
  Zoho: <Zoho className={classes.logoStyle} />,
  Xero: <Xero className={classes.logoStyle} />,
  Odoo: <Odoo className={classes.logoStyle} />,
  QuickBook: <QuickBook className={classes.logoStyle} />,
  TallyPrime: <Tally className={classes.logoStyle} />,
  // Excel: <XmlIcon className={classes.logoStyle} />,
};

const mergedIconsMap = { ...iconsMap, ...docuTranscribeIcon, ...exportIcon };

const initialNodes = [];
const initialEdges = [];

const createNodeTypes = (
  setNodes,
  setEdges,
  nodes,
  handleIconSelect,
  selectedIcon,
  selectedNode,
  setSelectedIcon,
  selectedAccount,
  setIsDraggedChanged,
  isFlowRunning,
) => ({
  customNode: (props) => (
    <CustomNodeType
      {...props}
      nodes={nodes}
      setNodes={setNodes}
      setEdges={setEdges}
      handleIconSelect={handleIconSelect}
      selectedIcon={selectedIcon}
      iconsMap={mergedIconsMap}
      selectedNode={selectedNode}
      setSelectedIcon={setSelectedIcon}
      selectedAccount={selectedAccount}
      setIsDraggedChanged={setIsDraggedChanged}
      isFlowRunning={isFlowRunning}
    />
  ),
});

const createEdgeTypes = (setEdges, setIsDraggedChanged) => ({
  step: (props) => (
    <CustomEdge
      {...props}
      setEdges={setEdges}
      setIsDraggedChanged={setIsDraggedChanged}
    />
  ),
});

const defaultAccountState = {
  id: '',
  type: '',
  email: '',
  token: '',
};
const defaultAccountStateTally = {
  id: '',
  type: '',
  ip_address: '',
  port: '',
  company: '',
  email: '',
};

const FlowConfigurationEditor = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isConnected = useSelector((states) => states.socketConfig.isConnected);
  const userDetail = useSelector((states) => states.user.user);
  const userId = userDetail?.id;
  const notifyUser = useSelector((states) => states.socket.notification);
  const notifyUploadedExported = notifyUser?.isUploadedExported;
  const notificationMessage = notifyUser?.message;
  const DocumentCompletedData = notifyUser?.status;
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const state = urlParams.get('state');
  const realmId = urlParams.get('realmId');
  const importOptionFromSessionStorage = sessionStorage.getItem(
    'importAutomatedOption',
  );
  const exportOptionFromSessionStorage = sessionStorage.getItem(
    'exportAutomatedOption',
  );
  const displayNameFromSessionStorage = sessionStorage.getItem(
    'displayNameForCredential',
  );
  const googleActions = [
    { value: 'NewAttachment', label: 'Email Attachment' },
    { value: 'Starred', label: 'Only Favorites' },
  ];
  const docutranscribeAction = [
    { value: 'sales', label: 'Sales Invoice' },
    { value: 'invoice', label: 'Direct Expense' },
    { value: 'receipt', label: 'InDirect Expense' },
    { value: 'BS', label: 'Bank Statement' },
    { value: 'CC', label: 'CreditCard Statement' },
  ];
  const microSoftAction = [{ value: 'NewFile', label: 'NewFile' }];
  const customAction = [{ value: 'Export Invoice', label: 'Export Invoice' }];

  const flowId = urlParams.get('flowId');
  const reactFlowWrapper = useRef(null);
  const [open, setOpen] = useState(false);
  const [odooForm, setOdooForm] = useState(false);
  const [openRightContainer, setOpenRightContainer] = useState(true);
  const [currentFlowIdData, setCurrentFlowIdData] = useState({});
  const [alignment, setAlignment] = useState('Action');
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [openDisplayNamePopup, setOpenDisplayNamePopup] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [displayNameRequiredError, setDisplayNameRequiredError] = useState('');
  const [actionsConfigure, setActionsConfigure] = useState({
    Gmail: { value: '', label: '' },
    GoogleDrive: { value: '', label: '' },
    Outlook: { value: '', label: '' },
    OneDrive: { value: '', label: '' },
    SharePoint: { value: '', label: '' },
    Docutranscribe: { value: '', label: '' },
    Odoo: { value: '', label: '' },
    Zoho: { value: '', label: '' },
    Xero: { value: '', label: '' },
    QuickBook: { value: '', label: '' },
    TallyPrime: { value: '', label: '' },
    // Excel: { value: '', label: '' },
  });
  const [options, setOptions] = useState([]);
  const [isFlowRunning, setIsFlowRunning] = useState(false);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState(null);
  const [isDraggedChanged, setIsDraggedChanged] = useState(false);
  const [automatedFlowSelected, setAutomatedFlowSelected] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState({
    Gmail: { ...defaultAccountState },
    GoogleDrive: { ...defaultAccountState },
    Outlook: { ...defaultAccountState },
    OneDrive: { ...defaultAccountState },
    SharePoint: { ...defaultAccountState },
    Odoo: { ...defaultAccountState },
    Zoho: { ...defaultAccountState },
    Xero: { ...defaultAccountState },
    QuickBook: { ...defaultAccountState },
    TallyPrime: { ...defaultAccountStateTally },
  });
  const [isExpanded, setIsExpanded] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingForTokenCallback, setIsLoadingForTokenCallback] =
    useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // InputOutputApplications list icons state
  const [uploadIcon, setUploadIcon] = useState(iconsMap);
  const [docutranscribeIcon, setDocutranscribeIcon] =
    useState(docuTranscribeIcon);
  const [exportOptionIcon, setExportOptionsIcon] = useState(exportIcon);
  const sections = [
    { id: 'upload', title: 'Upload', list: uploadIcon },
    { id: 'docutranscribe', title: 'Docutranscribe', list: docutranscribeIcon },
    { id: 'export', title: 'Export', list: exportOptionIcon },
  ];

  // state for oneDrive
  const [folderPath, setFolderPath] = useState(null);

  // state for Gmail
  const [filterData, setFilterData] = useState({
    Outlook: defaultFilterData,
    Gmail: defaultFilterData,
  });

  const [accounts, setAccounts] = useState([]);
  const [openTallyPrimeForm, setOpenTallyPrimeForm] = useState(false);
  const [openTaxPopup, setOpenTaxPopup] = useState(false);
  const [credentialId, setCredentialId] = useState();
  const [exportType, setExportType] = useState();
  const [credentialData, setCredentialData] = useState();

  const accountTypeMapping = {
    OneDrive: 'Microsoft Account',
    Outlook: 'Microsoft Account',
    SharePoint: 'Microsoft Account',
    GoogleDrive: 'Google Account',
    Gmail: 'Google Account',
    Zoho: 'Zoho Account',
    Xero: 'Xero Account',
    Odoo: 'Odoo Account',
    QuickBook: 'QuickBook Account',
    TallyPrime: 'TallyPrime',
  };

  const toggleSidebar = () => {
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    if (
      notificationMessage &&
      (DocumentCompletedData === 'Completed' || notifyUploadedExported)
    ) {
      Toast({ message: `${notificationMessage}`, variant: 'success' });

      dispatch(completeNotification({}));
    }
  }, [notificationMessage]);

  const handleAccounts = async (newValue) => {
    if (selectedIcon?.type === 'TallyPrime') {
      const payload = {
        ip_address: newValue.ip_address,
        id: newValue.id,
        type: newValue.type,
        port: newValue.port,
        company: newValue.company,
        email: `${newValue.ip_address}:${newValue.port}`,
      };
      setSelectedAccount((prev) => ({
        ...prev,
        [selectedIcon?.type]: payload,
      }));
    } else {
      setSelectedAccount((prev) => ({
        ...prev,
        [selectedIcon?.type]: newValue,
      }));
    }
    setIsDraggedChanged(true);
  };

  const fetchAllUser = async (selectedIcons) => {
    if (!selectedIcons) return; // Exit early if no selectedIcon
    const res = await getAllUserFromCredential(selectedIcons.type);
    if (res) {
      const filteredAccounts = res.map((account) => {
        const { id, type, credential, display_name } = account;
        switch (type) {
          case 'Zoho':
            return {
              id,
              type,
              email: credential.email,
              token: credential.refresh_token,
              flowId: account.flow_id,
              display_name,
            };
          case 'Xero':
            return {
              id,
              type,
              email: credential.email,
              token: credential.refresh_token,
              access_token: credential.access_token,
              id_token: credential.id_token,
              flowId: account.flow_id,
              display_name,
            };
          case 'Odoo':
            return {
              id,
              type,
              email: credential.username,
              username: credential.username,
              display_name,
            };
          case 'TallyPrime':
            return {
              id,
              type,
              email: `${credential?.ip_address}:${credential?.port}`,
              ip_address: credential?.ip_address,
              port: credential?.port,
              company: credential.company,
              display_name,
            };
          case 'QuickBook':
            return {
              id,
              type,
              email: credential.email,
              token: credential.accessToken,
              flowId: account.flow_id,
              display_name,
            };
          case 'Outlook':
            return {
              id,
              type,
              email: credential.email,
              token: credential.token,
              folderId: credential.folderId,
              driveId: credential.driveId,
              folderPath,
              display_name,
            };
          case 'SharePoint':
          case 'OneDrive':
            return {
              id,
              type,
              email: credential.email,
              token: credential.accessToken,
              folderId: credential.folderId,
              driveId: credential.driveId,
              folderPath,
              display_name,
            };
          case 'GoogleDrive':
            return {
              id,
              type,
              email: credential.email,
              token: credential.token,
              folderSelection: credential.folderSelection,
              display_name,
            };
          default:
            return {
              id,
              type,
              email: credential.email,
              token: credential.token,
              display_name,
            };
        }
      });
      setAccounts(filteredAccounts);
      return filteredAccounts;
    }
  };

  const handleZohoAndQuickBooksCallback = async () => {
    const handleRedirect = (url) => {
      window.location.href = url;
    };

    const handleTokenExchange = async (tokenData, type) => {
      const allAccounts = await fetchAllUser({ type });
      const accountWithEmailExists = allAccounts?.filter(
        (account) => account?.email === tokenData?.email,
      );
      if (accountWithEmailExists?.length > 0) {
        if (type === 'Zoho' || type === 'Xero' || type === 'QuickBook') {
          setCredentialData(tokenData);
          setCredentialId(accountWithEmailExists[0].id);
          setExportType(type);
          setOpenTaxPopup(true);
        } else if (type === 'OneDrive' || type === 'SharePoint') {
          setSelectedAccount((prev) => ({
            ...prev,
            [type]: accountWithEmailExists[0],
          }));
        }
      } else {
        let newCredential;
        if (tokenData?.email) {
          if (type === 'Zoho') {
            const {
              email,
              refresh_token: refreshToken,
              scope,
              api_domain: apiDomain,
            } = tokenData;
            newCredential = {
              type,
              credential: {
                email,
                refresh_token: refreshToken,
                scope,
                apiDomain,
              },
            };
          } else {
            newCredential = {
              type,
              credential: tokenData,
            };
          }
          setCredentialData(newCredential);
          const credential = await createManualAuthCredential(
            newCredential,
            false,
            displayNameFromSessionStorage,
          );
          if (type === 'Zoho' || type === 'Xero' || type === 'QuickBook') {
            setCredentialData(tokenData);
            setCredentialId(credential.id);
            setExportType(type);
            setOpenTaxPopup(true);
          } else if (type === 'OneDrive' || type === 'SharePoint') {
            setSelectedAccount((prev) => ({
              ...prev,
              [type]: {
                token: tokenData?.accessToken,
                email: tokenData?.email,
              },
            }));
            setAccounts((prev) => [
              ...prev,
              {
                type: selectedIcon?.type,
                token: tokenData?.accessToken,
                email: tokenData?.email,
                display_name: displayNameFromSessionStorage,
              },
            ]);
          }
          sessionStorage.removeItem('displayNameForCredential');
        }
      }
    };

    if (code) {
      const flowUrl = `${THIRD_PARTY_REDIRECT_URL_URL_FOR_AUTOMATED}?flowId=${state || ''}&code=${code}`;
      if (realmId) {
        if (state) {
          handleRedirect(`${flowUrl}&realmId=${realmId}`);
        } else {
          setIsLoadingForTokenCallback(true);
          const tokenData = await tokenExchangeForQB(
            code,
            realmId,
            'Automated',
          );
          if (tokenData) {
            await handleTokenExchange(tokenData, 'QuickBook');
          }
          setIsLoadingForTokenCallback(false);
        }
      } else if (state) {
        handleRedirect(flowUrl);
      } else {
        setIsLoadingForTokenCallback(true);
        if (exportOptionFromSessionStorage === 'Xero') {
          const tokenData = await tokenExchangeForXero(code, 'Automated');
          if (tokenData) {
            sessionStorage.removeItem('exportAutomatedOption');
            await handleTokenExchange(tokenData, 'Xero');
          }
        } else if (exportOptionFromSessionStorage === 'Zoho') {
          const tokenData = await tokenExchange(code, 'Automated');
          if (tokenData) {
            sessionStorage.removeItem('exportAutomatedOption');
            await handleTokenExchange(tokenData, 'Zoho');
          }
        } else if (importOptionFromSessionStorage === 'OneDrive') {
          const tokenData = await tokenExchangeForMicrosoft(code, 'Automated');
          if (tokenData) {
            sessionStorage.removeItem('importAutomatedOption');
            await handleTokenExchange(tokenData, 'OneDrive');
            setIsDraggedChanged(true);
          }
          setIsLoadingForTokenCallback(false);
        } else if (importOptionFromSessionStorage === 'SharePoint') {
          const tokenData = await tokenExchangeForMicrosoft(code, 'Automated');
          if (tokenData) {
            sessionStorage.removeItem('importAutomatedOption');
            await handleTokenExchange(tokenData, 'SharePoint');
            setIsDraggedChanged(true);
          }
          setIsLoadingForTokenCallback(false);
        }
      }
    }
  };
  const handleNewAndExport = async (
    tokenData,
    type,
    accountSetter,
    setLoader,
  ) => {
    if (type === 'QuickBook') {
      const allAccounts = await fetchAllUser({ type: 'QuickBook' });
      const accountWithEmailExists = allAccounts?.filter(
        (account) => account?.email === tokenData?.email,
      );
      if (
        accountWithEmailExists?.length > 0 &&
        accountWithEmailExists[0].flowId
      ) {
        accountSetter((prev) => ({
          ...prev,
          QuickBook: accountWithEmailExists[0],
        }));
      } else {
        accountSetter((prev) => ({
          ...prev,
          QuickBook: {
            token: tokenData?.accessToken,
            accessToken: tokenData?.accessToken,
            refreshToken: tokenData?.refreshToken,
            realmId: tokenData?.realmId,
            email: tokenData?.email,
          },
        }));
        setAccounts((prev) => [
          ...prev,
          {
            type: 'QuickBook',
            token: tokenData?.accessToken,
            accessToken: tokenData?.accessToken,
            refreshToken: tokenData?.refreshToken,
            email: tokenData?.email,
            realmId: tokenData?.realmId,
          },
        ]);
      }
      setIsDraggedChanged(true);
    } else if (tokenData?.email) {
      const allAccounts = await fetchAllUser({ type });
      const accountWithEmailExists = allAccounts?.filter(
        (account) => account?.email === tokenData?.email,
      );
      if (
        accountWithEmailExists?.length > 0 &&
        accountWithEmailExists[0].flowId
      ) {
        accountSetter((prev) => ({
          ...prev,
          [type]: accountWithEmailExists[0],
        }));
      } else {
        accountSetter((prev) => ({
          ...prev,
          [type]: {
            token: tokenData?.refresh_token,
            access_token: tokenData?.access_token,
            refresh_token: tokenData?.refresh_token,
            scope: tokenData?.scope,
            expires_at: tokenData?.expires_at,
            id_token: tokenData?.id_token,
            api_domain: tokenData.api_domain,
            email: tokenData?.email,
          },
        }));
        setAccounts((prev) => [
          ...prev,
          {
            type,
            token: tokenData?.refresh_token,
            access_token: tokenData?.access_token,
            email: tokenData?.email,
            scope: tokenData?.scope,
            api_domain: tokenData.api_domain,
            expires_at: tokenData?.expires_at,
            id_token: tokenData?.id_token,
          },
        ]);
      }
      setIsDraggedChanged(true);
      setLoader(false);
    } else {
      Toast({ message: `${tokenData?.message}`, variant: 'error' });
    }
  };
  useEffect(() => {
    handleZohoAndQuickBooksCallback();
  }, [code]);

  const handleChange = (event, newAlignment) => {
    setAlignment(newAlignment);
  };
  const handleClickOpen = () => {
    setOpen(true);
    setAutomatedFlowSelected(true);
  };

  const closeOdooForm = (_, reason) => {
    if (reason !== 'backdropClick') {
      setOdooForm(false);
    }
  };
  const CloseTallyForm = (_, reason) => {
    if (reason !== 'backdropClick') {
      setOpenTallyPrimeForm(false);
    }
  };

  const handleOdooAuth = (credential) => {
    if (credential) {
      setSelectedAccount((prev) => ({
        ...prev,
        [selectedIcon?.type]: {
          email: credential?.username,
          uid: credential?.uid,
          db: credential?.db,
          password: credential?.password,
          url: credential?.url,
          username: credential?.username,
        },
      }));
      setAccounts((prev) => [
        ...prev,
        {
          type: selectedIcon?.type,
          email: credential?.username,
          uid: credential?.uid,
          db: credential?.db,
          password: credential?.password,
          url: credential?.url,
          username: credential?.username,
        },
      ]);
      setIsDraggedChanged(true);
    }
  };

  const handleTallyAuth = (data) => {
    if (data) {
      setSelectedAccount((prev) => ({
        ...prev,
        [selectedIcon?.type]: {
          ip_address: data?.credential.ip_address,
          company: data?.credential.company,
          port: data?.credential?.port,
          email: `${data.credential?.ip_address}:${data.credential?.port}`,
        },
      }));
      setAccounts((prev) => [
        ...prev,
        {
          type: selectedIcon?.type,
          ip_address: data?.credential?.ip_address,
          company: data?.credential.company,
          port: data?.credential?.port,
          email: `${data.credential?.ip_address}:${data.credential?.port}`,
        },
      ]);
      setIsDraggedChanged(true);
    }
  };

  const { instance } = useMsal();

  const authHandlerForMicrosoft = async () => {
    const result = await openAuthenticationEmailPicker({ instance });

    if (!result) {
      return;
    }

    const { accessTokenFromMicrosoft, accountEmailId } = result;

    if (!accessTokenFromMicrosoft || !accountEmailId) {
      return;
    }
    const accountWithEmailExists = accounts?.filter(
      (account) => account?.email === accountEmailId,
    );
    if (accessTokenFromMicrosoft && accountEmailId) {
      if (accountWithEmailExists?.length > 0) {
        setSelectedAccount((prev) => ({
          ...prev,
          [selectedIcon?.type]: accountWithEmailExists[0],
        }));
      } else {
        setSelectedAccount((prev) => ({
          ...prev,
          [selectedIcon?.type]: {
            token: accessTokenFromMicrosoft,
            email: accountEmailId,
          },
        }));
        setAccounts((prev) => [
          ...prev,
          {
            type: selectedIcon?.type,
            token: accessTokenFromMicrosoft,
            email: accountEmailId,
          },
        ]);
      }
      setIsDraggedChanged(true);
    }
  };

  const showPathHandler = async () => {
    const { odDriveId, odFolderId } = await openPathSelectionPicker(
      selectedAccount[selectedIcon?.type]?.token,
    );
    if (odDriveId && odFolderId) {
      setFolderPath(null);
      setSelectedAccount((prev) => ({
        ...prev,
        [selectedIcon?.type]: {
          ...prev[selectedIcon?.type],
          driveId: odDriveId,
          folderId: odFolderId,
        },
      }));

      setAccounts((prev) => {
        const updatedAccounts = prev.map((account) =>
          account.type === selectedIcon?.type
            ? { ...account, driveId: odDriveId, folderId: odFolderId }
            : account,
        );
        // If the account type doesn't exist, add a new entry
        const accountExists = prev.some(
          (account) => account.type === selectedIcon?.type,
        );
        return accountExists
          ? updatedAccounts
          : [
              ...updatedAccounts,
              {
                type: selectedIcon?.type,
                driveId: odDriveId,
                folderId: odFolderId,
              },
            ];
      });
    }
  };

  useEffect(() => {
    const getFolderPath = async () => {
      const account = selectedAccount[selectedIcon?.type];
      if (
        account?.email &&
        account?.token &&
        account?.driveId &&
        account?.folderId
      ) {
        const responsePath = await fetchFolderDetails(
          account.driveId,
          account.folderId,
          account.token,
        );
        setSelectedAccount((prev) => ({
          ...prev,
          [selectedIcon?.type]: {
            ...prev[selectedIcon?.type],
            folderPath: responsePath?.name,
          },
        }));
        setAccounts((prev) => {
          const updatedAccounts = prev.map((allAccount) =>
            allAccount.type === selectedIcon?.type
              ? { ...account, folderPath: responsePath?.name }
              : account,
          );
          // If the account type doesn't exist, add a new entry
          const accountExists = prev.some(
            (allAccount) => allAccount.type === selectedIcon?.type,
          );
          return accountExists
            ? updatedAccounts
            : [
                ...updatedAccounts,
                { type: selectedIcon?.type, folderPath: responsePath?.name },
              ];
        });
        setFolderPath(responsePath?.name);
        if (folderPath !== responsePath?.name) {
          setIsDraggedChanged(true);
        }
      }
    };

    if (!folderPath) {
      getFolderPath();
    }
  }, [
    selectedAccount[selectedIcon?.type]?.email,
    selectedAccount[selectedIcon?.type]?.token,
    selectedAccount[selectedIcon?.type]?.driveId,
    selectedAccount[selectedIcon?.type]?.folderId,
  ]);

  const onNodeClick = (event, node) => {
    if (!isFlowRunning) {
      setSelectedNode(node); // Update the selected node state
      setOpenRightContainer(true);
      setAlignment('Action');
    }
  };

  const onPaneClick = () => {
    if (!isFlowRunning) {
      setSelectedNode(null); // Reset the selected node state
    }
  };

  // Google Authentication
  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      const codes = tokenResponse.code;

      try {
        const result = await getAllGoogleToken(codes);
        if (result) {
          const userEmail = result.username;
          const accountWithEmailExists = accounts?.filter(
            (account) => account.email === userEmail,
          );
          if (accountWithEmailExists?.length > 0) {
            const newResult = {
              ...accountWithEmailExists[0],
              refreshToken: result.refreshToken,
              expireDate: result.expireDate,
            };
            setSelectedAccount((prev) => ({
              ...prev,
              [selectedIcon?.type]: newResult,
            }));
          } else {
            setSelectedAccount((prev) => ({
              ...prev,
              [selectedIcon?.type]: {
                token: result.accessToken,
                email: userEmail,
                refreshToken: result.refreshToken,
                expireDate: result.expireDate,
              },
            }));
            setAccounts((prev) => [
              ...prev,
              {
                type: selectedIcon?.type,
                token: result.accessToken,
                email: userEmail,
                refreshToken: result.refreshToken,
                expireDate: result.expireDate,
              },
            ]);
          }
        }
        setIsDraggedChanged(true);
      } catch (error) {
        console.error('Error while fetching user info from Google:', error);
      }
    },
    onFailure: (error) => {
      console.error('Google login failed:', error);
    },
    access_type: 'offline',
    prompt: 'consent',
    flow: 'auth-code',
    scope:
      selectedIcon?.type === 'Gmail'
        ? ['https://www.googleapis.com/auth/gmail.readonly']
        : ['https://www.googleapis.com/auth/drive.readonly'],
  });

  // Google folder selection
  const googleDrivePickerHandler = async () => {
    const selectedFolder = await googleDriveFolderPath(
      selectedAccount?.GoogleDrive?.token,
    );
    if (selectedFolder) {
      setSelectedAccount((prev) => ({
        ...prev,
        [selectedIcon?.type]: {
          ...prev[selectedIcon?.type],
          folderSelection: selectedFolder,
        },
      }));

      setAccounts((prev) => {
        const updatedAccounts = prev.map((account) =>
          account.type === selectedIcon?.type
            ? { ...account, folderSelection: selectedFolder }
            : account,
        );
        // If the account type doesn't exist, add a new entry
        const accountExists = prev.some(
          (account) => account.type === selectedIcon?.type,
        );
        return accountExists
          ? updatedAccounts
          : [
              ...updatedAccounts,
              { type: selectedIcon?.type, folderSelection: selectedFolder },
            ];
      });
      setIsDraggedChanged(true);
    }
  };

  const handleIconSelect = (type) => {
    setSelectedIcon(type);
    setIsDraggedChanged(true);
  };

  const handleDisplayNamePopupClose = () => {
    setOpenDisplayNamePopup(false);
    setDisplayNameRequiredError('');
  };

  const handleActionChange = (event) => {
    setActionsConfigure((prev) => ({
      ...prev,
      [selectedIcon?.type]: event.target.value,
    }));
    setIsDraggedChanged(true);
  };

  const selectedIcons = () => {
    switch (selectedIcon?.type) {
      case 'Gmail':
        return 'Google Gmail';
      case 'GoogleDrive':
        return 'Google Drive';
      case 'Outlook':
        return 'Outlook';
      case 'OneDrive':
        return 'MicroSoft OneDrive';
      case 'SharePoint':
        return 'MicroSoft SharePoint';
      case 'Odoo':
        return 'Odoo';
      case 'Docutranscribe':
        return 'Docutranscribe';
      case 'Zoho':
        return 'Zoho';
      case 'Xero':
        return 'Xero';
      case 'QuickBook':
        return 'Quick Book';
      case 'TallyPrime':
        return 'TallyPrime';
      // case 'Excel':
      //   return 'Excel';
      default:
        return 'Select an Icon';
    }
  };

  const rightContainerIcons = () => {
    switch (selectedIcon?.type) {
      case 'Gmail':
        return <GmailIcon className={classes.logoStyle} />;
      case 'GoogleDrive':
        return <GoogleDriveIcon className={classes.logoStyle} />;
      case 'Outlook':
        return <OutlookIcon className={classes.logoStyle} />;
      case 'OneDrive':
        return <OneDriveIcon className={classes.logoStyle} />;
      case 'SharePoint':
        return <SharePointIcon className={classes.logoStyle} />;
      case 'Odoo':
        return <Odoo className={classes.logoStyle} />;
      case 'Docutranscribe':
        return <LogoIcon className={classes.logoStyle} />;
      case 'Zoho':
        return <Zoho className={classes.logoStyle} />;
      case 'Xero':
        return <Xero className={classes.logoStyle} />;
      case 'QuickBook':
        return <QuickBook className={classes.logoStyle} />;
      case 'TallyPrime':
        return <Tally className={classes.logoStyle} />;
      default:
        return <Box />;
    }
  };

  useEffect(() => {
    if (
      selectedIcon?.type === 'OneDrive' ||
      selectedIcon?.type === 'GoogleDrive'
    ) {
      setOptions(microSoftAction);
    } else if (
      selectedIcon?.type === 'Odoo' ||
      selectedIcon?.type === 'Zoho' ||
      selectedIcon?.type === 'Xero' ||
      selectedIcon?.type === 'QuickBook' ||
      selectedIcon?.type === 'TallyPrime'
    ) {
      setOptions(customAction);
    } else if (selectedIcon?.type === 'Docutranscribe') {
      setOptions(docutranscribeAction);
    } else {
      setOptions(googleActions);
    }
  }, [selectedIcon, isFlowRunning]);

  const changeFlowStatus = async (newStatus) => {
    const statusUpdate = await editInvoiceConfigurationFlow(flowId, newStatus);
    if (statusUpdate.status === 200) {
      const data = await getFlowById(flowId);
      setIsFlowRunning((prev) => !prev);
      setCurrentFlowIdData(data?.editorData);
      logInfo(
        {
          data: newStatus === 'Running' ? 'Process start' : 'Process stop',
          label: 'success',
        },
        flowId,
      );
    }
  };

  const validateAccount = (account, fields) => {
    const missingFields = fields.filter((field) => !account?.[field]);
    return { isValid: missingFields.length === 0, missingFields };
  };

  const isNodeConnected = () => {
    const isOneDrive = checkDocutranscribeNode(nodes, edges, 'OneDrive');
    const isSharePoint = checkDocutranscribeNode(nodes, edges, 'SharePoint');
    const isOutlook = checkDocutranscribeNode(nodes, edges, 'Outlook');
    const isGmail = checkDocutranscribeNode(nodes, edges, 'Gmail');
    const isGoogleDrive = checkDocutranscribeNode(nodes, edges, 'GoogleDrive');

    return {
      isOneDrive,
      isSharePoint,
      isOutlook,
      isGmail,
      isGoogleDrive,
    };
  };

  const showMissingFieldError = (serviceName, missingFields) => {
    if (missingFields.length > 0) {
      const field = missingFields[0];
      const errorMessage =
        field === 'email'
          ? `Email is required for ${serviceName}`
          : `${field.charAt(0).toUpperCase() + field.slice(1)} selection is required for ${serviceName}`;
      Toast({ message: errorMessage, variant: 'warning' });
      return true; // Indicate that an error was shown
    }
    return false; // No error was shown
  };

  const handleFlowRunning = async () => {
    const currentFlowStatus = currentFlowIdData?.status;
    const newStatus = currentFlowStatus === 'Stopped' ? 'Running' : 'Stopped';

    if (currentFlowStatus !== 'Running' && isDraggedChanged) {
      Toast({ message: 'Please Save Changes First', variant: 'warning' });
      return;
    }

    if (!nodes?.length) {
      if (
        currentFlowStatus === 'Running' &&
        actionsConfigure?.Docutranscribe?.value
      ) {
        await changeFlowStatus(newStatus);
      }
      return;
    }

    const nodeLabels = new Set(
      nodes.map((singleNode) => singleNode.data.label),
    );
    const hasDocutranscribe = nodeLabels.has('Docutranscribe');

    if (!hasDocutranscribe) {
      Toast({
        message: 'Please Connect DocuTranscribe Node to Import Node.',
        variant: 'warning',
      });
      return;
    }

    if (!actionsConfigure?.Docutranscribe?.value) {
      Toast({
        message: 'Please Select document type from DocuTranscribe',
        variant: 'warning',
      });
      return;
    }

    const { isOneDrive, isSharePoint, isOutlook, isGmail, isGoogleDrive } =
      isNodeConnected();

    if (
      !isOneDrive &&
      !isSharePoint &&
      !isOutlook &&
      !isGmail &&
      !isGoogleDrive
    ) {
      Toast({
        message: 'No input node is connected to DocuTranscribe',
        variant: 'warning',
      });
      return;
    }

    const validateAndShowError = () => {
      const validations = [
        {
          service: 'OneDrive',
          validation: validateAccount(selectedAccount?.OneDrive, [
            'email',
            'token',
            'driveId',
            'folderId',
          ]),
          isActive: isOneDrive,
        },
        {
          service: 'SharePoint',
          validation: validateAccount(selectedAccount?.SharePoint, [
            'email',
            'token',
            'driveId',
            'folderId',
          ]),
          isActive: isSharePoint,
        },
        {
          service: 'Outlook',
          validation: validateAccount(selectedAccount?.Outlook, [
            'email',
            'token',
          ]),
          isActive: isOutlook,
        },
        {
          service: 'Google Drive',
          validation: validateAccount(selectedAccount?.GoogleDrive, [
            'email',
            'token',
            'folderSelection',
          ]),
          isActive: isGoogleDrive,
        },
        {
          service: 'Gmail',
          validation: validateAccount(selectedAccount?.Gmail, [
            'email',
            'token',
          ]),
          isActive: isGmail,
        },
      ];

      return validations.some(({ service, validation, isActive }) => {
        if (isActive && !validation.isValid) {
          return showMissingFieldError(service, validation.missingFields);
        }
        return false;
      });
    };

    if (validateAndShowError()) {
      return;
    }

    const startFlow = async (uploadFlowFunction) => {
      await createToken(flowId);
      await changeFlowStatus(newStatus);
      await uploadFlowFunction();
    };

    if (isOneDrive || isSharePoint || isOutlook) {
      await startFlow(microSoftUploadFlow);
    } else if (isGoogleDrive || isGmail) {
      await startFlow(googleUploadFlow);
    }
  };

  const handleClose = (_, reason) => {
    if (reason !== 'backdropClick') {
      setOpen(false);
    }
  };
  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('type');
      const { offsetX, offsetY } = event.nativeEvent;

      const newNode = {
        id: `${type}_${Date.now()}`,
        type: 'customNode',
        position: { x: offsetX, y: offsetY },
        data: { label: type },
      };
      setNodes((nds) => nds.concat(newNode));
      setIsDraggedChanged(true);
      handleIconSelect({ type, id: newNode.id }); // Call handleIconSelect with the type
    },
    [setNodes],
  );

  const handleNodesChange = useCallback(
    (changes) => {
      if (!isFlowRunning) {
        onNodesChange(changes);
        if (nodes?.length !== changes?.length) {
          setIsDraggedChanged(true);
        }
      }
    },
    [onNodesChange, nodes],
  );

  const handleConnect = (params) => {
    if (!isFlowRunning) {
      setEdges((eds) =>
        addEdge({ ...params, type: 'step', data: { setEdges } }, eds),
      );
      setIsDraggedChanged(true);
    }
  };

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    const modifiedEvent = { ...event };
    modifiedEvent.dataTransfer.dropEffect = 'move';
  }, []);
  const memoizedNodeTypes = useMemo(
    () =>
      createNodeTypes(
        setNodes,
        setEdges,
        nodes,
        handleIconSelect,
        selectedIcon,
        selectedNode,
        setSelectedIcon,
        selectedAccount,
        setIsDraggedChanged,
        isFlowRunning,
      ),
    [
      setNodes,
      setEdges,
      selectedIcon,
      selectedNode,
      setSelectedIcon,
      selectedAccount,
      setIsDraggedChanged,
      isFlowRunning,
    ],
  );

  const memoizedEdgeTypes = useMemo(
    () => createEdgeTypes(setEdges, setIsDraggedChanged),
    [setEdges, setIsDraggedChanged],
  );

  const credentialArrayFunction = (allNode) => {
    const validLabels = [
      'Zoho',
      'Xero',
      'QuickBook',
      'Odoo',
      'TallyPrime',
      'SharePoint',
      'OneDrive',
      'Outlook',
      'Gmail',
      'GoogleDrive',
    ];

    return allNode.reduce((acc, node) => {
      const label = node.data?.label;
      if (!label || !validLabels.includes(label)) return acc;

      const account = selectedAccount[label];
      if (label !== 'TallyPrime') {
        if (!account || !account.email) return acc;
      }
      if (label === 'TallyPrime') {
        if (!account || !account.ip_address) return acc;
      }
      let credential = null;

      if (
        ['SharePoint', 'OneDrive', 'Outlook', 'Gmail', 'GoogleDrive'].includes(
          label,
        )
      ) {
        if (account.token) {
          credential = { ...account };
        }
      } else if (label === 'Zoho') {
        credential = {
          email: account.email,
          refresh_token: account.refresh_token,
          scope: account.scope,
          apiDomain: account.api_domain,
        };
      } else if (label === 'TallyPrime') {
        credential = {
          ip_address: account.ip_address,
          port: account.port,
          company: account.company,
        };
      } else {
        credential = account;
      }

      if (credential) {
        acc.push({ label, credential });
      }

      return acc;
    }, []);
  };

  const handleSaveChanges = async () => {
    const credentialsArray = credentialArrayFunction(nodes);
    if (!isFlowRunning) {
      setIsLoading(true);
      const data = {
        node: nodes,
        edge: edges,
        selectedIcon,
        actionsConfigure,
        filterData,
        selectedAccount,
        folderPath,
      };
      await updateDetails(flowId, data, credentialsArray, displayName);
      setDisplayName('');
      if (code) {
        navigate(`/flow-configuration/editor?flowId=${flowId}`);
      }
      // setSelectedAccount(prev => ({
      //   ...prev,
      //   OneDrive: { ...defaultAccountState },
      //   TallyPrime: { ...defaultAccountStateTally },
      // }))
      setIsDraggedChanged(false);
      setIsLoading(false);
    } else {
      Toast({
        message: 'Please Stop The Running Flow First',
        variant: 'warning',
      });
    }
  };

  useEffect(() => {
    if (isDraggedChanged) {
      handleSaveChanges();
    }
  }, [isDraggedChanged, nodes]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const data = await getFlowById(flowId);
      if (data) {
        setIsLoading(false);
        const status = data?.editorData?.status;
        const { details } = data?.editorData || {};
        if (status === 'Running') {
          setIsFlowRunning(true);
        }
        if (details) {
          setIsLoading(false);
          setNodes(details.node);
          setEdges(details.edge);
          setSelectedIcon(details.selectedIcon);
          setActionsConfigure(details.actionsConfigure);
          setFilterData(details.filterData);
          setSelectedAccount(details.selectedAccount);
          setFolderPath(details?.folderPath);
        }
        setCurrentFlowIdData(data?.editorData);
      }
    };

    fetchData();
  }, []);

  // add api in services
  useEffect(() => {
    fetchAllUser(selectedIcon);
  }, [selectedIcon, setAccounts]);

  useEffect(() => {
    const filteredIcons = Object.fromEntries(
      Object.entries(iconsMap).filter(([key]) =>
        key.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    );
    setUploadIcon(filteredIcons);

    const filteredDocuIcon = Object.fromEntries(
      Object.entries(docuTranscribeIcon).filter(([key]) =>
        key.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    );
    setDocutranscribeIcon(filteredDocuIcon);

    const filteredExportIcons = Object.fromEntries(
      Object.entries(exportIcon).filter(([key]) =>
        key.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    );
    setExportOptionsIcon(filteredExportIcons);
  }, [searchQuery]);

  const hasData = sections.some(
    (section) => Object.keys(section.list).length > 0,
  );

  const addAccountDisplayName = () => {
    setDisplayName('');
    setOpenDisplayNamePopup(true);
  };

  const addAccountHandler = async (selectedIconForAddAccount) => {
    // setDisplayName('');
    // setOpenDisplayNamePopup(true);
    const typeHandlers = {
      Zoho: async () => {
        const responseUrl = await zohoAuthentication('Automated');
        if (responseUrl) {
          sessionStorage.setItem(
            'exportAutomatedOption',
            selectedIconForAddAccount?.type,
          );
          window.location.href = `${responseUrl.url}&state=${flowId}`;
        }
      },
      Xero: async () => {
        const responseUrl = await xeroAuthentication('Automated');
        if (responseUrl) {
          sessionStorage.setItem(
            'exportAutomatedOption',
            selectedIconForAddAccount?.type,
          );
          window.location.href = `${responseUrl.url}&state=${flowId}`;
        }
      },
      QuickBook: () => {
        const { scope } = exportQB;
        const authUri = `https://appcenter.intuit.com/connect/oauth2?client_id=${QB_CLIENT_ID}&response_type=code&redirect_uri=${encodeURIComponent(THIRD_PARTY_REDIRECT_URL_URL_FOR_AUTOMATED)}&scope=${encodeURIComponent(scope)}&state=${flowId}`;
        window.open(authUri, '_self');
      },
      Odoo: () => setOdooForm(true),
      Gmail: handleGoogleLogin,
      GoogleDrive: handleGoogleLogin,
      SharePoint: async () => {
        const responseUrl = await microsoftAuthentication('Automated');
        if (responseUrl) {
          sessionStorage.setItem(
            'importAutomatedOption',
            selectedIconForAddAccount?.type,
          );
          window.location.href = `${responseUrl.url}&state=${flowId}`;
        }
      },
      OneDrive: async () => {
        const responseUrl = await microsoftAuthentication('Automated');
        if (responseUrl) {
          sessionStorage.setItem(
            'importAutomatedOption',
            selectedIconForAddAccount?.type,
          );
          window.location.href = `${responseUrl.url}&state=${flowId}`;
        }
      },
      Outlook: authHandlerForMicrosoft,
      // Excel: authHandlerForMicrosoft,
      TallyPrime: () => setOpenTallyPrimeForm(true),
    };
    const handler = typeHandlers[selectedIconForAddAccount.type];
    if (handler) {
      await handler();
    }
  };

  const handleDeleteAccounts = async () => {
    const allAccounts = await fetchAllUser(selectedIcon);
    const selectedEmail = selectedAccount[selectedIcon?.type]?.email;
    const selectedId = selectedAccount[selectedIcon?.type]?.id;

    if (!selectedEmail) {
      Toast({ message: 'Please Select account First', variant: 'warning' });
      return;
    }

    const deleteAccount = async (id) => {
      const response = await deleteUserFromCredential(id);
      if (response) {
        setSelectedAccount((prev) => ({
          ...prev,
          [selectedIcon?.type]:
            selectedIcon?.type === 'TallyPrime'
              ? { ...defaultAccountStateTally }
              : { ...defaultAccountState },
        }));
        setIsDraggedChanged(true);
        fetchAllUser(selectedIcon);
      }
    };

    if (selectedId) {
      await deleteAccount(selectedId);
      setIsDraggedChanged(true);
    } else {
      const findAccount = allAccounts?.find(
        (acc) => acc.email === selectedEmail && acc.id,
      );

      if (findAccount) {
        await deleteAccount(findAccount.id);
        if (
          findAccount?.type === 'OneDrive' ||
          findAccount?.type === 'SharePoint'
        ) {
          setFolderPath(null);
        }
        setIsDraggedChanged(true);
      }
    }
  };

  useEffect(() => {
    if (userId && !isConnected && currentFlowIdData?.status === 'Running') {
      dispatch(connectSocket(userId));
    } else if (currentFlowIdData?.status === 'Stopped' && isConnected) {
      // dispatch(disconnectSocket());
    }
  }, [dispatch, currentFlowIdData, userId, isConnected]);

  useEffect(
    () => () => {
      if (isConnected) {
        // dispatch(disconnectSocket());
      }
    },
    [dispatch],
  );
  const closePopup = () => {
    setOpenTaxPopup(false);
  };
  return (
    <Root className={classes.root}>
      <ReactFlowProvider>
        {isLoadingForTokenCallback || isLoading ? (
          <Backdrop
            className={classes.editFlowLoaderBox}
            open={isLoading || isLoadingForTokenCallback}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        ) : null}
        <Box
          display="flex"
          flexDirection="column"
          height="calc(100vh - 90px)"
          overflow="auto"
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Breadcrumbs>
              <Link
                underline="hover"
                href="/flow-configuration"
                onClick={() => navigate('/flow-configuration')}
              >
                <Typography variant="h4" color={lightPalette.darkPurple.main}>
                  Flows
                </Typography>
              </Link>
              <Typography variant="h4" color={lightPalette.darkPurple.main}>
                {currentFlowIdData?.name}
              </Typography>
            </Breadcrumbs>
            <Box display="flex" alignItems="flex-start">
              <Button
                variant="contained"
                className={classes.viewLogButton}
                onClick={() => navigate(`/log-dashboard/${flowId}`)}
                startIcon={<Visibility height={16} width={16} />}
              >
                View Logs
              </Button>
              <Button
                className={classes.flowButton}
                variant="contained"
                disabled={
                  selectedIcon === null &&
                  currentFlowIdData?.status === 'Stopped'
                }
                color={isFlowRunning ? 'error' : 'primary'}
                onClick={handleFlowRunning}
                startIcon={
                  isFlowRunning ? (
                    <StopArrowIcon height={16} width={16} />
                  ) : (
                    <PlayArrowIcon height={16} width={16} />
                  )
                }
              >
                {isFlowRunning ? 'Stop Flow' : 'Start Flow'}
              </Button>
            </Box>
          </Box>
          <Box height="100%" overflow="auto" marginTop="8px">
            <Box className={classes.mainBoxForInvoiceFlow}>
              {/* Left Container (Sidebar) */}
              <Box
                width={isExpanded ? sidebarWidth : collapsedWidth}
                className={classes.leftSideBarBox}
              >
                <Box display="flex" justifyContent="end">
                  <Button
                    className={classes.toggleButton}
                    onClick={toggleSidebar}
                  >
                    {isExpanded ? (
                      <KeyboardDoubleArrowLeftIcon />
                    ) : (
                      <KeyboardDoubleArrowRightSharpIcon />
                    )}
                  </Button>
                </Box>
                {isExpanded && (
                  <TextField
                    size="small"
                    fullWidth
                    className={classes.textFieldPadding}
                    placeholder="Search here..."
                    variant="outlined"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                <Box
                  className={`${classes.dropDownBox} ${isExpanded ? classes.expandedHeight : 'auto'}`}
                >
                  {sections.map(
                    (section) =>
                      Object.keys(section.list).length > 0 && (
                        <InputOutputApplications
                          key={section.id}
                          title={section.title}
                          list={section.list}
                          isExpanded={isExpanded}
                          isFlowRunning={isFlowRunning}
                        />
                      ),
                  )}
                  {!hasData && (
                    <Typography variant="body2" className={classes.noDataFound}>
                      No Data Found
                    </Typography>
                  )}
                </Box>
              </Box>
              {/* Middle Container */}
              <Box
                id="middle-container"
                className={classes.middleContainerBox}
                onDragOver={onDragOver}
                onDrop={onDrop}
              >
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={handleNodesChange}
                  onEdgesChange={!isFlowRunning ? onEdgesChange : null}
                  onConnect={(params) => handleConnect(params)}
                  nodeTypes={memoizedNodeTypes}
                  edgeTypes={memoizedEdgeTypes}
                  zoomOnScroll={false}
                  onNodeClick={onNodeClick}
                  onPaneClick={onPaneClick}
                  ref={reactFlowWrapper}
                  className={classes.scrollBar}
                >
                  <Background color="#00000" variant={BackgroundVariant.Dots} />
                </ReactFlow>
                <Controls className="flow-controls" position="bottom-right" />
              </Box>
              {/* Right Container */}
              {openRightContainer && nodes.length > 0 && (
                // && selectedIcon?.type !== 'Excel'
                <Box
                  minWidth="30%"
                  height="100%"
                  overflow="auto"
                  borderLeft={`1px solid ${lightPalette.lunarBase.medium}`}
                >
                  <Box
                    p={2}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    bgcolor={lightPalette.japanBlush.main}
                  >
                    <Box className={classes.rightContainerIconBox}>
                      {rightContainerIcons()}
                      <Typography
                        variant="h5"
                        noWrap
                        component="div"
                        color={lightPalette.darkPurple.main}
                        ml={1}
                      >
                        {selectedIcons()}
                      </Typography>
                    </Box>
                    <CloseIcon
                      className={classes.sidebarCloseAction}
                      onClick={() => setOpenRightContainer(false)}
                    />
                  </Box>
                  <Box px={2}>
                    <Box mb={2}>
                      <TabContext value={alignment}>
                        <StyledTabList
                          onChange={handleChange}
                          aria-label="tabs"
                        >
                          <Tabs
                            label={
                              <Typography
                                variant="body1"
                                color={
                                  alignment === 'Action'
                                    ? lightPalette.primary.main
                                    : lightPalette.darkPurple.main
                                }
                              >
                                Actions
                              </Typography>
                            }
                            value="Action"
                          />
                          {(selectedIcon?.type === 'Gmail' ||
                            selectedIcon?.type === 'Outlook') && (
                            <Tabs
                              label={
                                <Typography
                                  variant="body1"
                                  color={
                                    alignment === 'Configuration'
                                      ? lightPalette.primary.main
                                      : lightPalette.darkPurple.main
                                  }
                                >
                                  Configuration
                                </Typography>
                              }
                              value="Configuration"
                            />
                          )}
                        </StyledTabList>
                      </TabContext>
                      {alignment === 'Action' ? (
                        <Box>
                          <Typography
                            variant="body1"
                            color={lightPalette.darkPurple.main}
                            fontWeight={400}
                            mt={2}
                            mb={1}
                          >
                            Select Action
                          </Typography>
                          <Select
                            fullWidth
                            size="small"
                            IconComponent={KeyboardArrowDownIcon}
                            value={actionsConfigure[selectedIcon?.type] || ''}
                            onChange={handleActionChange}
                            disabled={isFlowRunning}
                            renderValue={(selected) => {
                              if (
                                !selected?.label ||
                                selected?.label?.length === 0
                              ) {
                                return (
                                  <Typography
                                    variant="body1"
                                    color={lightPalette.shoeWax.semiDark}
                                  >
                                    Select Action
                                  </Typography>
                                );
                              }
                              return (
                                <Typography
                                  variant="body1"
                                  color={lightPalette.shoeWax.semiDark}
                                >
                                  {selected?.label}
                                </Typography>
                              );
                            }}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: '50vh',
                                },
                              },
                            }}
                          >
                            {options?.map((option) => (
                              <MenuItem key={option.id} value={option}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </Box>
                      ) : (
                        <FormControl
                          variant="outlined"
                          className={classes.textField}
                          disabled={isFlowRunning}
                        >
                          <Typography
                            variant="body1"
                            mt={2}
                            mb={1.5}
                            color={lightPalette.darkPurple.main}
                            fontWeight={400}
                          >
                            Configuration
                          </Typography>
                          <OutlinedInput
                            size="small"
                            placeholder="Not Configured"
                            endAdornment={
                              <InputAdornment position="end">
                                <TuneIcon
                                  onClick={handleClickOpen}
                                  cursor="pointer"
                                />
                              </InputAdornment>
                            }
                          />
                        </FormControl>
                      )}
                    </Box>
                    <Box>
                      {alignment === 'Action' &&
                        actionsConfigure[selectedIcon?.type]?.value &&
                        selectedIcon?.type !== 'Docutranscribe' && (
                          <>
                            <Typography
                              variant="body1"
                              color={lightPalette.darkPurple.main}
                              fontWeight={400}
                            >
                              {accountTypeMapping[selectedIcon?.type] ||
                                'Account'}
                            </Typography>
                            <Box
                              className={classes.selectAndAddAccountContainer}
                            >
                              <Autocomplete
                                value={selectedAccount[selectedIcon?.type]}
                                size="small"
                                className={classes.selectAccountAutocomplete}
                                isOptionEqualToValue={(option, newValue) =>
                                  option?.email === newValue?.email
                                }
                                onChange={(event, newValue) => {
                                  handleAccounts(newValue);
                                }}
                                disabled={isFlowRunning}
                                options={accounts}
                                getOptionLabel={(option) =>
                                  option.display_name
                                    ? option.display_name
                                    : option.email
                                }
                                renderInput={(params) => {
                                  const endAdornmentData =
                                    params.InputProps.endAdornment;
                                  return (
                                    <TextField
                                      {...params}
                                      placeholder="Select Account"
                                      InputProps={{
                                        ...params.InputProps,
                                        endAdornment:
                                          !isFlowRunning && endAdornmentData,
                                      }}
                                    />
                                  );
                                }}
                                renderOption={(props, option) => (
                                  <li {...props}>
                                    {option.inputValue ? (
                                      <Box>
                                        <Box>
                                          <AddIcon />
                                        </Box>
                                        Add &ldquo;
                                        {option.inputValue}
                                        &rdquo;
                                      </Box>
                                    ) : (
                                      <Typography variant="body1">
                                        {option.display_name
                                          ? option.display_name
                                          : option.email}
                                      </Typography>
                                    )}
                                  </li>
                                )}
                              />
                              <CustomTooltip title="Add account">
                                <Box className={classes.addAccountIconBox}>
                                  <AddUserIcon
                                    onClick={() => {
                                      if (
                                        selectedIcon?.type === 'TallyPrime' ||
                                        selectedIcon?.type === 'Odoo'
                                      ) {
                                        addAccountHandler(selectedIcon);
                                      } else {
                                        addAccountDisplayName();
                                      }
                                    }}
                                    className={classes.addUserAccountIcon}
                                  />
                                </Box>
                              </CustomTooltip>
                              <CustomTooltip title="Delete Account">
                                <DeleteAccount
                                  className={classes.deleteAccountIcon}
                                  onClick={handleDeleteAccounts}
                                />
                              </CustomTooltip>
                            </Box>
                          </>
                        )}
                    </Box>
                    {accounts?.length > 0 &&
                      selectedAccount[selectedIcon?.type]?.email &&
                      (selectedIcon?.type === 'GoogleDrive' ||
                        selectedIcon?.type === 'OneDrive' ||
                        selectedIcon?.type === 'SharePoint') && (
                        <Box mt={2}>
                          <Typography
                            variant="body1"
                            color={lightPalette.darkPurple.main}
                            fontWeight={400}
                          >
                            Folder Location
                          </Typography>
                          <TextField
                            variant="outlined"
                            size="small"
                            fullWidth
                            placeholder={
                              !folderPath ||
                              !selectedAccount[selectedIcon?.type]
                                ?.folderSelection
                                ? 'Select Folder'
                                : null
                            }
                            value={
                              selectedIcon?.type === 'GoogleDrive'
                                ? selectedAccount[selectedIcon?.type]
                                    ?.folderSelection?.name
                                : folderPath
                            }
                            className={classes.textFieldFolderOpenIcon}
                            disabled={isFlowRunning}
                            InputProps={{
                              readOnly: true,
                              endAdornment: (
                                <InputAdornment position="end">
                                  <FolderOpenIcon
                                    onClick={
                                      selectedIcon?.type === 'GoogleDrive'
                                        ? googleDrivePickerHandler
                                        : showPathHandler
                                    }
                                    className={globalClasses.cursorPointerClass}
                                  />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Box>
                      )}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        <FilterPopUp
          open={open}
          handleClose={handleClose}
          setFilterData={setFilterData}
          automatedFlowSelected={automatedFlowSelected}
          selectedType={selectedIcon?.type}
          filterData={filterData}
          setIsDraggedChanged={setIsDraggedChanged}
        />
        <OdooForm
          odooForm={odooForm}
          closeOdooForm={closeOdooForm}
          selectedIcon={selectedIcon}
          handleOdooNewAuthManualAndExport={handleOdooAuth}
        />
        <TallyPrimeForm
          tallyForm={openTallyPrimeForm}
          closeTallyForm={CloseTallyForm}
          selectedIcon={selectedIcon}
          isManual={false}
          handleNewAndExport={handleTallyAuth}
        />
        {openTaxPopup && (
          <TaxMapping
            exportType={exportType}
            credentialId={credentialId}
            newCredential={credentialData}
            isManual={false}
            openTaxPopup={openTaxPopup}
            closePopup={closePopup}
            handleNewAndExport={handleNewAndExport}
            accountSetter={setSelectedAccount}
            setLoader={setIsLoadingForTokenCallback}
          />
        )}
        <DisplayNamePopUp
          open={openDisplayNamePopup}
          handleClose={handleDisplayNamePopupClose}
          setDisplayName={setDisplayName}
          displayName={displayName}
          displayNameRequiredError={displayNameRequiredError}
          setDisplayNameRequiredError={setDisplayNameRequiredError}
          addAccountHandler={addAccountHandler}
          selectedIcon={selectedIcon}
        />
      </ReactFlowProvider>
    </Root>
  );
};

export default FlowConfigurationEditor;
